import Global from '../../store/global-selectors';
import moment from 'moment-timezone';
import { CostReviewHelperService } from '../../services/cost-review-helper-service';
import { emptyArray } from 'ui-components/default-props/empty';
import { InsuranceProviders } from '../../enums/insurance-providers';
import { AMERICA_LOS_ANGELES } from '../../constants/timezone-iana-names';
import { noShipsuranceBillingInfoErrors } from '../../services/insurance-service';

const bySellerFulfillmentProvider = (state, sellerFulfillmentProviderId) => {
  const sellerFulfillmentProviders = Global.selectSellerActiveFulfillmentProviders(state);
  return sellerFulfillmentProviders.find((fillProv) => fillProv.sellerFulfillmentProviderId === sellerFulfillmentProviderId);
};

const byFulfillmentProvider = (state, fulfillmentProviderId) => {
  const sellerFulfillmentProviders = Global.selectSellerActiveFulfillmentProviders(state);
  return sellerFulfillmentProviders.find((fillProv) => fillProv.fulfillmentProviderId === fulfillmentProviderId);
};

const mapProvidersWithServices = (providers, state) => {
  const sellerProviders = Global.selectSellerLabelProviders(state);
  const sellerServices = Global.selectSellerServices(state);
  const sellerFulfillmentServices = Global.selectSellerFulfillmentServices(state);
  const serviceReferenceLookup = Global.selectReferenceDatagetServiceReferenceLookup(state);

  return providers.map((provider) => {
    if (provider.fulfillmentProviderId) {
      const fulfillmentProvider =
        bySellerFulfillmentProvider(state, provider.sellerFulfillmentProviderId) ||
        byFulfillmentProvider(state, provider.fulfillmentProviderId);
      const lineItemFillProvider = { ...fulfillmentProvider, ...provider };
      const services = (provider.services || emptyArray).map((service) => {
        const sellerFulfillmentService = sellerFulfillmentServices.find((serv) => serv.fulfillmentServiceId === service.shippingServiceId);
        return {
          ...service,
          serviceName: sellerFulfillmentService?.name,
        };
      });
      return {
        ...lineItemFillProvider,
        services,
      };
    }
    const labelProvider = sellerProviders.find((prov) => prov.sellerLabelProviderId === provider.sellerLabelProviderId);
    const lineItemProvider = { ...labelProvider, ...provider };
    const services = (provider.services || emptyArray).map((service) => {
      // TODO: add sellerLabelProviderId to this check when the back-end returns it (isn't at this time)
      const sellerDataService = sellerServices.find((serv) => serv.serviceId === service.shippingServiceId);
      return {
        ...service,
        serviceName: sellerDataService?.name ?? serviceReferenceLookup[service.shippingServiceId]?.name,
      };
    });
    return {
      ...lineItemProvider,
      services,
    };
  });
};

const isContinueDisabled = (costsMetaData, costReviewData, state) => {
  const costReviewDataIsEmpty = Object.keys(costReviewData || {}).length === 0;
  if (costReviewDataIsEmpty) {
    return true;
  }
  const purchaseIsLoading = Global.selectBulkPurchasePostageIsLoading(state) || false;
  const blockContinue = costsMetaData.some((meta) => !meta.canContinue);
  return purchaseIsLoading || blockContinue;
};

const hasEnoughFunds = (costsMetaData) => {
  return costsMetaData.every((meta) => {
    const { insuranceProviderId } = meta;
    // HACK SPD-9694: Shipsurance insuranceProvider id backdoor
    if (
      insuranceProviderId &&
      (insuranceProviderId === InsuranceProviders.Shipsurance.insuranceProviderId || insuranceProviderId === '0')
    ) {
      return meta.sufficientBalance;
    }
    return !meta.providerMaintainsBalance || meta.sufficientBalance;
  });
};

export const CostReviewBaseContainer = (costReviewData = {}, state) => {
  const userHasPurchasingRole = Global.selectUserhasPurchasingRole(state);
  const hasPersonalShipsurance = Global.selectSellerPersonalShipsuranceAccount(state);
  const userDateFormat = Global.selectUserUserDateFormat(state);
  const billingInfoErrorMessage = Global.selectBulkInsurancePurchaseErrorMessage(state);
  const userHasBillingInfoSetup = !noShipsuranceBillingInfoErrors.some((err) => billingInfoErrorMessage?.includes(err));

  const providerCosts = costReviewData.providerCosts || emptyArray;
  const walletCosts = costReviewData.walletCosts || emptyArray;
  const insuranceCosts = costReviewData.insuranceCosts || emptyArray;
  const costWarnings = costReviewData.costWarnings || emptyArray;

  const providersWithServices = mapProvidersWithServices(providerCosts, state);
  const providers = providersWithServices.filter((p) => !p.walletId);
  const walletedProviderGroups = walletCosts.map((walletProviderCosts) => mapProvidersWithServices(walletProviderCosts, state));

  const allProviders = [...insuranceCosts, ...providerCosts];
  const allCostsMeta = allProviders.map((p) => ({
    ...p,
    ...CostReviewHelperService.costReviewLineItemProviderMeta(p, userHasPurchasingRole, hasPersonalShipsurance),
  }));
  const balanceMaintained = allCostsMeta.some((meta) => meta.providerMaintainsBalance);

  const { shipFromCount, suggestedShipDate } = costReviewData;

  // Note: Currently the backend sends the suggesstedShipDate in SS Time
  // (i.e. Pacific Time), using moment tz gives the UI the context of
  // what timezone this value is in - Greg S. 6/8/22
  const shipDate = moment.tz(suggestedShipDate, AMERICA_LOS_ANGELES).format(userDateFormat);
  return {
    userDateFormat,
    userHasBillingInfoSetup,
    providers,
    walletedProviderGroups,
    insurances: insuranceCosts,
    balanceMaintained,
    enoughFunds: hasEnoughFunds(allCostsMeta),
    isContinueDisabled: isContinueDisabled(allCostsMeta, costReviewData, state),
    costWarnings,
    shipDate,
    shipFromCount,
  };
};
